import { useEffect, useMemo, useRef, useState } from 'react';
import { useAsyncCallback } from 'react-async-hook';
import useDeepCompareEffect from 'use-deep-compare-effect';
import { useIntl } from 'react-intl';

import { investorApiClient } from 'api';
import {
  SecondaryInvestmentFilterModal,
  SecondaryInvestmentFilterPayload,
  SecondaryInvestmentFilterValues,
  secondaryInvestmentFiltersHelper,
} from 'components/filters/secondary-investment';
import { ALL_CURRENCIES_VALUE } from 'components/filters/constants';
import { useCurrencySelection } from 'providers/currency-selection';
import { useQuery, useLoadMoreController } from 'hooks';
import { ConfirmationDialog, EndOfTableMessage, ScrollToTopButton, TableLayout } from 'components';

import { TableFilters } from '../components';
import { SecondaryInvestmentHeader } from './SecondaryInvestmentHeader';
import { SecondaryInvestmentRecord, SecondaryInvestmentRecordSkeleton } from './SecondaryInvestmentRecord';
import { SecondaryInvestment, ShareSaleStatus } from 'types/secondary-market';

interface Props {
  wrapperRef?: HTMLElement | null;
  tableLayout: boolean;
}

type QueryLike = Record<string, unknown>;

type SecondaryTabQuery = {
  currency?: string[];
  havingSaleStatus?: ShareSaleStatus[];
} & QueryLike;

const normalizeStringArray = (value: unknown): string[] | undefined => {
  if (Array.isArray(value)) return value.filter((v): v is string => typeof v === 'string' && v.length > 0);
  if (typeof value === 'string' && value.length > 0) return [value];
  return undefined;
};

const normalizeSaleStatus = (value: unknown): ShareSaleStatus[] | undefined => {
  if (!value) return undefined;
  const arr = Array.isArray(value) ? value : [value];
  return arr.filter((v): v is ShareSaleStatus => typeof v === 'string');
};

const toSecondaryTabQuery = (parsedQuery: QueryLike): SecondaryTabQuery => ({
  ...parsedQuery,
  currency: normalizeStringArray(parsedQuery.currency),
  havingSaleStatus: normalizeSaleStatus(parsedQuery.havingSaleStatus),
});

const resolveCurrencyFilter = (
  queryCurrency: string[] | undefined,
  selectedCurrency: string | undefined
): string[] | undefined => {
  if (queryCurrency?.includes(ALL_CURRENCIES_VALUE)) return undefined;
  if (queryCurrency && queryCurrency.length > 0) return queryCurrency;
  return selectedCurrency ? [selectedCurrency] : undefined;
};

export const SecondaryMarketTab = ({ wrapperRef, tableLayout }: Props) => {
  const { parsedQuery, push } = useQuery();
  const intl = useIntl();
  const { selectedCurrency, currencyOptions } = useCurrencySelection();

  const tabQuery = useMemo(
    () => toSecondaryTabQuery(parsedQuery as unknown as QueryLike),
    [parsedQuery]
  );

  const [data, setData] = useState<SecondaryInvestment[]>([]);
  const [continuationToken, setContinuationToken] = useState<string | null>(null);
  const [total, setTotal] = useState<number | null>(null);
  const [sellShareToConfirm, setSellShareToConfirm] = useState<SecondaryInvestment | null>(null);
  const previousSelectedCurrency = useRef<string | undefined>();
  const hasLoadedFirstPage = useRef(false);
  
  useEffect(() => {
    if (!selectedCurrency) {
      return;
    }
    const didContextChange = previousSelectedCurrency.current !== selectedCurrency;
    const hasCurrencyFilter = (tabQuery.currency?.length ?? 0) > 0;
    const shouldUpdate = didContextChange || !hasCurrencyFilter;

    if (shouldUpdate && !(tabQuery.currency?.length === 1 && tabQuery.currency[0] === selectedCurrency)) {
      push({
        ...tabQuery,
        currency: [selectedCurrency],
      });
    }
      previousSelectedCurrency.current = selectedCurrency;
  }, [push, selectedCurrency, tabQuery]);

  const fetchInvestments = useAsyncCallback(async (query: SecondaryTabQuery, fromBeginning?: boolean) => {
    if (!fromBeginning && !hasLoadedFirstPage.current && continuationToken === null) return;
    if (!fromBeginning && continuationToken === 'EOF') return;

    if (fromBeginning) {
      setData([]);
      setContinuationToken(null);
      setTotal(null);
      hasLoadedFirstPage.current = false;
    }

    const currency = resolveCurrencyFilter(query.currency, selectedCurrency);
    const havingSaleStatus = query.havingSaleStatus?.length ? query.havingSaleStatus : [ShareSaleStatus.Open];

    const { currency: _currency, havingSaleStatus: _havingSaleStatus, ...rest } = query;

    const { data: resp } =
      await investorApiClient.investors.investorApiControllerGetInvestorsSecondarymarketOffersByContinuationToken({
        ...rest,
        currency,
        havingSaleStatus,
        continuationToken: fromBeginning ? undefined : continuationToken ?? undefined,
      } as any);

    const mapped: SecondaryInvestment[] = resp.data.map((shareSell: any) => ({
      ...shareSell,
      id: shareSell.shareSale.id,
    }));

    setData((state) => (fromBeginning ? mapped : state.concat(mapped)));
    setContinuationToken(resp.continuationToken ?? null);
    setTotal((state) => (typeof state === 'number' ? state : resp.total));
    hasLoadedFirstPage.current = true;
  });

  const cancelSell = useAsyncCallback(async (inv: SecondaryInvestment) => {
    await investorApiClient.investors.investorApiControllerCancelInvestmentSale(
      inv.shareSale.providerId,
      inv.shareSale.dealId
    );
    fetchInvestments.execute(tabQuery, true);
  });

  const handleCloseConfirmationModal = useAsyncCallback(async (accepted) => {
    if (accepted && sellShareToConfirm) {
      await cancelSell.execute(sellShareToConfirm);
    }
    setSellShareToConfirm(null);
  });

  useDeepCompareEffect(() => {
    fetchInvestments.execute(tabQuery, true);
  }, [tabQuery, selectedCurrency]);

  useLoadMoreController({
    wrapperRef,
    loading: fetchInvestments.loading,
    continuationToken,
    fetch: (q) => fetchInvestments.execute(q, false),
    query: tabQuery,
  });

  return (
    <>
      <TableFilters<SecondaryInvestmentFilterPayload, SecondaryInvestmentFilterValues>
        components={{
          TableHeader: SecondaryInvestmentHeader,
          AllFiltersModal: SecondaryInvestmentFilterModal,
        }}
        isSecondary
        helpers={secondaryInvestmentFiltersHelper}
        initialFilters={tabQuery}
        resetValues={{
          currency: selectedCurrency ? [selectedCurrency] : [],
        }}
        currencyOptions={currencyOptions}
        contextCurrency={selectedCurrency}
        onFiltersChange={push}
        total={total}
        loading={fetchInvestments.loading}
        tableLayout={tableLayout}
      />

      <TableLayout
        components={{
          Record: SecondaryInvestmentRecord,
          RecordSkeleton: SecondaryInvestmentRecordSkeleton,
        }}
        data={data}
        dataLoading={fetchInvestments.loading}
        tableLayout={tableLayout}
        onCancelSell={setSellShareToConfirm}
      />

      {continuationToken === 'EOF' && <EndOfTableMessage />}
      <ScrollToTopButton wrapperRef={wrapperRef} />
      <ConfirmationDialog
        open={!!sellShareToConfirm}
        strings={{
          title: intl.formatMessage({ id: 'are_you_sure' }),
          subtitle: intl.formatMessage({ id: 'canceled_secondary_market_deal_will_be_removed' }),
          cancelText: intl.formatMessage({ id: 'no' }),
          acceptText: intl.formatMessage({ id: 'boolean.true' }),
        }}
        onClose={handleCloseConfirmationModal.execute}
        loading={cancelSell.loading || handleCloseConfirmationModal.loading}
      />
    </>
  );
};
